import { transformDateToLocalDateString } from "helpers";
import { FleetVehicleRow } from "services/api/fleet/vehicles/types";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const FUEL_TYPE_VARIANT: Record<string, BadgeVariant> = {
  B: "warning",
  P: "warning",
  D: "info",
  E: "success",
  H: "success-dark",
  LPG: "danger",
  CNG: "danger",
};

const TRANSMISSION_VARIANT: Record<string, BadgeVariant> = {
  A: "info",
  M: "warning",
  S: "success",
};

const pickVariant = (
  map: Record<string, BadgeVariant>,
  code: string | null | undefined,
  fallback: BadgeVariant
): BadgeVariant => (code && map[code.toUpperCase()]) || fallback;

/**
 * Kolone marketplace pretrage. Namjerno bez internih podataka iz flote
 * (nabavna cijena, marže, status, VIN, akcije) — kupac vidi samo ono što je
 * javno, uz prodajnu cijenu bez razrade.
 */
export const MARKETPLACE_VEHICLE_SEARCH_COLUMNS = (
  t: (key: string) => string
): Column[] => [
    {
      title: "",
      key: "image",
      field: "image",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <TableIcon
          imageUrl={row.picture}
          initials={row.vehicleManufacturer?.name?.toString().charAt(0).toUpperCase()}
          imageFit="cover"
        />
      ),
    },
    {
      title: "admin.fleet.vehicle.search.manufacturer",
      key: "manufacturer",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <div className="flex items-center gap-2">
          {row.vehicleManufacturer && (
            <img
              src={row.vehicleManufacturer?.image ?? ""}
              alt=""
              className="w-12 flex-shrink-0 rounded-full object-cover"
            />
          )}
          <div>{row.vehicleManufacturer?.name}</div>
        </div>
      ),
    },
    {
      title: "admin.fleet.vehicle.search.model",
      key: "model",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        const year = row.vehicleModel?.year ?? null;

        return (
          <div>
            <div className="text-md mb-1">{row.vehicleModel?.name}</div>
            {year && (
              <div className="text-xs text-slate-500">
                {t("admin.fleet.vehicle.search.vehiclemodel.year")}: {year}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.type",
      key: "type",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        const parts = [
          row.vehicleType?.maximumPowerKw != null || row.vehicleType?.maximumPowerHp != null
            ? `${row.vehicleType?.maximumPowerKw}kW / ${row.vehicleType?.maximumPowerHp} KS`
            : null,
          row.vehicleType?.drivenWheels?.name ?? null,
        ].filter(Boolean);

        return (
          <div>
            <div className="text-md mb-0.5">{row.vehicleType?.name}</div>
            {parts.length > 0 && <div className="text-xs text-slate-500">{parts.join(" · ")}</div>}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.fueltype",
      key: "fuelType",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) =>
        row.vehicleType?.fuelType ? (
          <Badge
            text={row.vehicleType.fuelType.name ?? ""}
            variant={pickVariant(FUEL_TYPE_VARIANT, row.vehicleType.fuelType.id, "warning")}
            height="small"
            showDot
          />
        ) : null,
    },
    {
      title: "admin.fleet.vehicle.search.transmission",
      key: "transmission",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) =>
        row.vehicleType?.transmission ? (
          <Badge
            text={row.vehicleType.transmission.name ?? ""}
            variant={pickVariant(TRANSMISSION_VARIANT, row.vehicleType.transmission.id, "info")}
            height="small"
            showDot
          />
        ) : null,
    },
    {
      title: "admin.fleet.vehicle.search.currentkm",
      key: "currentKm",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <div className="text-md">
          {row.kmTraveled != null ? `${row.kmTraveled.toLocaleString("hr-HR")} km` : "-"}
        </div>
      ),
    },
    {
      title: "admin.fleet.vehicle.search.firstregistration",
      key: "firstRegistration",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <div className="text-md">
          {row.firstRegistrationDate ? transformDateToLocalDateString(row.firstRegistrationDate) : "-"}
        </div>
      ),
    },
    {
      title: "admin.fleet.vehicle.search.location",
      key: "location",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <div className="text-sm text-slate-900 truncate">{row.location?.locationName ?? "-"}</div>
      ),
    },
    {
      title: "admin.fleet.vehicle.search.sellingprice",
      key: "sellingPrice",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => (
        <div className="text-sm font-bold text-green-600">
          {row.price?.sellingPrice ? `${row.price.sellingPrice.toLocaleString("hr-HR")} €` : "-"}
        </div>
      ),
    },
  ];
